import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import {
  enterRoundValue,
  INITIAL_OPERATOR_ROUNDS,
  submitRound,
  type OperatorRoundState,
} from './OperatorRoundEngine';

type OperatorRoundContextValue = {
  rounds: OperatorRoundState;
  /** Sets one reading's entry on the round a RECORD task names. */
  enter: (taskId: string, rowId: string, value: string) => void;
  /** Submits the round at the session cursor. */
  submit: (taskId: string) => void;
};

const OperatorRoundContext = createContext<OperatorRoundContextValue | null>(null);

/**
 * Project-level holder of the Operator's rounds (polish.P16 round 1), mounted
 * beside `EscalationDecisionProvider` so a round outlives the Record & submit
 * card: switching the landing's view, or the persona, and back keeps the
 * entries and the submission. Only a reload resets them.
 */
export function OperatorRoundProvider({ children }: { children: ReactNode }) {
  const [rounds, setRounds] = useState<OperatorRoundState>(INITIAL_OPERATOR_ROUNDS);
  const { state } = useScenario();
  const enter = useCallback(
    (taskId: string, rowId: string, value: string) =>
      setRounds((current) => enterRoundValue(current, taskId, rowId, value, state.cursor)),
    [state.cursor],
  );
  const submit = useCallback(
    (taskId: string) => setRounds((current) => submitRound(current, taskId, state.cursor)),
    [state.cursor],
  );
  const value = useMemo(() => ({ rounds, enter, submit }), [rounds, enter, submit]);
  return <OperatorRoundContext.Provider value={value}>{children}</OperatorRoundContext.Provider>;
}

export function useOperatorRounds(): OperatorRoundContextValue {
  const ctx = useContext(OperatorRoundContext);
  if (!ctx) throw new Error('useOperatorRounds must be used inside <OperatorRoundProvider>');
  return ctx;
}
